import { useState, useEffect, useMemo } from "react";
import { useReactTable, getCoreRowModel, getExpandedRowModel, getSortedRowModel } from "@tanstack/react-table";
import { useSaveToStorage, loadFromStorage } from "../utils";
import { useProcesses } from "../scripts/queries/useProcessQueries";
import { DateFormatter, FormatCompany } from "../utils";
import exportToCSV from "../utils/ExportExcel";

// Avoid a new array render when query data is undefined
const EMPTY_ARRAY = [];

export const useProcess = () => {
    const persisted = loadFromStorage('PROCESS_FILTERS') || {};
    const [search, setSearch] = useState(persisted.search || "");
    const [sorting, setSorting] = useState(persisted.sorting || []);
    const [expanded, setExpanded] = useState(persisted.expanded || {});
    const [customers, setCustomers] = useState([]);
    const [resources, setResources] = useState([]);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [activeOption, setActiveOption] = useState(persisted.activeOption || []);
    const [secondOption, setSecondOption] = useState(persisted.secondOption || [])

    const {data: processes = EMPTY_ARRAY, refetch: fetchProcesses, isFetching, isLoading: loading, dataUpdatedAt} = useProcesses();
    
    useSaveToStorage('PROCESS_FILTERS', {search, sorting, expanded, activeOption, secondOption})

    useEffect(() => {
        if (dataUpdatedAt) {
            setLastUpdate(new Date(dataUpdatedAt));
        }
    }, [dataUpdatedAt]);

     const formattedData = useMemo(() => {
        const now = new Date();

        return processes.map(item => {

            //Sort details by their beginning date
            const sortedDetails = item.detalles ? [...item.detalles].sort((a, b) =>
                new Date(a.inicio) - new Date(b.inicio)
            ) : [];

            const rawEntry = new Date(item.ingreso);

            let entry = "";
            let updated = "";

            if (!isNaN(rawEntry) ) {
                const diffMs = now - rawEntry;

                if (diffMs < 0) {
                    entry = "Pendiente";
                    updated = "Pendiente";
                } else {
                    const diffMin = Math.floor(diffMs / 60000);
                    const diffHours = Math.floor(diffMin / 60);
                    const diffDays = Math.floor(diffHours / 24);

                    if (diffDays >= 7) {
                        const compact = DateFormatter(rawEntry, 'COMPACT_DATE');
                        const dateOnly = compact.split(',')[0];
                        entry = dateOnly;
                        updated = dateOnly;
                    } else if (diffDays >= 1) {
                        entry = `Hace ${diffDays} ${diffDays === 1 ? 'día' : 'días'}`
                        updated = `Hace ${diffDays}d`;
                    } else {
                        entry = diffHours >= 1 ? `Hace ${diffHours} hrs` : 'Hoy';
                        updated = diffHours >= 1 ? `Hace ${diffHours}h` : `Hace ${diffMin}m`;
                    }
                }
            }

            //Estado field logic
            let status = "Ingresada";

            if (rawEntry && rawEntry <= now && sortedDetails.length > 0) {
                const startedDetails = sortedDetails
                    .filter(d => new Date(d.inicio) <= now)
                    .sort((a, b) => new Date(b.inicio) - new Date(a.inicio))

                if(startedDetails.length > 0) {
                    status = startedDetails[0].recurso;
                }
            }

            const deliveryDate = new Date(item.entrega);
            let delivery = "";
            let deliveryColor = 'ONED';

            if (!isNaN(deliveryDate)) {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const compareDate = new Date(deliveryDate);
                compareDate.setHours(0, 0, 0, 0);

                const diffDays = Math.ceil((compareDate - today) / (1000 * 60 * 60 * 24));

                if (diffDays < 0) {
                    deliveryColor = 'Red';
                } else if (diffDays <= 1) {
                    deliveryColor = 'Orange';
                }

                if (compareDate.getTime() === today.getTime()) {
                    delivery = "Hoy";
                } else if (diffDays === 1) {
                    delivery = "Mañana";
                } else {
                    delivery = deliveryDate.toLocaleDateString('es-MX', {day: '2-digit', month: '2-digit', year: 'numeric'})
                }
            }

            //Use the raw date values for the sorting
            const rawEntryMs = !isNaN(rawEntry) ? rawEntry.getTime() : 0;
            const rawDeliveryMs = !isNaN(deliveryDate) ? deliveryDate.getTime() : 0; 

            //Clean the names to avoid duplicates (e.g. 3M S.A. de C.V., 3M)
            const formatCliente = FormatCompany(item.cliente);

            // console.log(`Actualizado: ${item.actualizado}`)
            // console.log(`Actualizado formateado: ${item.actualizado}`)
            return {...item, detalles: sortedDetails, cliente: formatCliente, ingreso: entry, actualizado: updated, estado: status, entrega: delivery, entregaColor: deliveryColor, ingresoMs: rawEntryMs, entregaMs: rawDeliveryMs}
        })
    }, [processes]);

    useEffect(() => {
        setCustomers([...new Set(formattedData.map(p => p.cliente))]);
        setResources([...new Set(formattedData.map(r => r.estado))]);
    }, [formattedData])

    const filteredData = useMemo(() => {
        return formattedData.filter(item => {
            const searchLower = search.toLowerCase();

            //Search match
            const matches = !search || String(item.numOrdem).toLowerCase().includes(searchLower)

            //Client match
            const matchesClient = activeOption.length === 0 || activeOption.includes(item.cliente);
            const matchesResource = secondOption.length === 0 || secondOption.includes(item.estado);
            return matches && matchesClient && matchesResource;
        })
    }, [formattedData, search, activeOption, secondOption]);

    const dateSort = (field) => (rowA, rowB) => {
        const timeA = rowA.original[field] || 0;
        const timeB = rowB.original[field] || 0;
        return timeA - timeB;
    }

    const columns = [
        {accessorKey: 'numOrdem', header: 'OP', width: '132px'},
        {accessorKey: 'cliente', header: 'Cliente', width: '348px'},
        {accessorKey: 'ingreso', header: 'Ingreso', width: '232px', sortingFn: dateSort('ingresoMs')},
        {accessorKey: 'estado', header: 'Estado', width: '172px'},
        {accessorKey: 'actualizado', header: 'Actualizado', width: '172px', sortingFn: dateSort('ingresoMs')},
        {accessorKey: 'entrega', header: 'Entrega almacén', width: '160px', sortingFn: dateSort('entregaMs')},
    ].filter(Boolean);

    const table = useReactTable({
        data: filteredData,
        columns,
        state:{expanded, sorting},
        onExpandedChange: setExpanded,
        onSortingChange: setSorting,
        enableSortingRemoval: true,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getRowCanExpand: (row) => row.original.detalles?.length > 0,
    });

    const exportCSV = () => {
        const csvRows = [];

        const breakdownKeys = ['recurso', 'descripcion', 'elemento', 'inicio', 'fin', 'cantPlanif', 'cantProd']
        const breakdownHeaders = ['Recurso', 'Descripcion', 'Elemento', 'Inicio', 'Fin', 'C. Planif', 'C. Prod']

        const headers = columns.map((col) => col.header);
        csvRows.push(headers); //Add the main headers to the csv

        filteredData.forEach((row) => {
            const values = columns.map((col) => row[col.accessorKey] || "");
            csvRows.push(values);

            //Add the breakdown data, including headers
            if (row.detalles && row.detalles.length > 0) {
                csvRows.push(breakdownHeaders);

                row.detalles.forEach((detail) => {
                    const detailRow = breakdownKeys.map((key) => detail[key] || "");
                    csvRows.push(detailRow);
                });
                csvRows.push([]);
            }
        });

        exportToCSV("Informacion_Procesos.xlsx", csvRows);
    }

    return {search, activeOption, secondOption, customers, resources, table, isFetching, loading, lastUpdate, fetchProcesses, setSearch, setActiveOption, setSecondOption, setCustomers, exportCSV};
}